import { USD_EXAM_SECTIONS } from '@/types/shared';
import type { ChipTone } from '@/components/common/StatusChip';
import type { BookingStatus, ExamSection } from '@/types/shared';

/** Chip tone per `BookingStatus` — mirrors `lib/studentStatus.ts`'s pattern. `Requested`/`Approved`
 * (2026-07 approval workflow) read as in-progress states before the section is finally Booked. */
const TONE_BY_BOOKING_STATUS: Record<BookingStatus, ChipTone> = {
  'Not Booked': 'neutral',
  Requested: 'warning',
  Approved: 'info',
  Booked: 'info',
  Completed: 'success',
  Cancelled: 'danger',
};

export function toneForBookingStatus(status: BookingStatus): ChipTone {
  return TONE_BY_BOOKING_STATUS[status] ?? 'neutral';
}

/** E6: `bookedCount` = Booked + Completed; `Not Booked` and `Cancelled` both count as "remaining". */
export function isBookedStatus(status: BookingStatus): boolean {
  return status === 'Booked' || status === 'Completed';
}

/** E4: only `Booked` requires an `examDate` — the server 400s otherwise; mirrored here so the
 * client blocks Save (with an inline field error) instead of relying on the round-trip. */
export function requiresExamDate(status: BookingStatus): boolean {
  return status === 'Booked';
}

/** T126 (17-finance-ux.md §6.1) — a reservation `reserveDate` is required for ALL 4 sections the
 * moment the resulting status is `Booked` (distinct from `examDate`, which stays optional). */
export function requiresReserveDate(status: BookingStatus): boolean {
  return status === 'Booked';
}

/** T126 — only SAT/ACT (USD-priced) require a `usdEgpRate` on reservation; EST I/II (EGP-priced,
 * rate implicitly 1) never accept one (§4.2/§6.1 — the field is absent from the DOM entirely). */
export function requiresUsdRate(examSection: ExamSection): boolean {
  return (USD_EXAM_SECTIONS as readonly string[]).includes(examSection);
}
